import { log } from '@/log'
import { ClientResolver } from '@/services/api/free-contents/client-resolver'
import { LoadSessionData } from '@/services/session/load-session-data'
import { DataFulfilledSessionCookiePayload } from '@/services/session/session-cookie'

export interface ReplaceReferralUrlDeps {
  readonly getSessionPayloadWithDataFulfilled: () => Promise<DataFulfilledSessionCookiePayload>
  readonly location: Pick<Location, 'protocol' | 'host'>
}
const defaultReplaceReferralUrlDeps: ReplaceReferralUrlDeps = {
  getSessionPayloadWithDataFulfilled: () => LoadSessionData.getInstance().load(),
  location,
}

export async function buildReplaceReferralUrl(
  deps: ReplaceReferralUrlDeps = defaultReplaceReferralUrlDeps
): Promise<ClientResolver> {
  /**
   * 入力文字列中に含まれるリファラルURL用マーカーを、実際のURLに変換する
   */
  return deps.getSessionPayloadWithDataFulfilled().then(
    (payload: DataFulfilledSessionCookiePayload) =>
      payload.data.isRegistered
        ? resolveMarkerWithInviter(payload.data.userId, deps.location)
        // 未ログインユーザーの場合、招待リンクとしては機能させない
        : resolveMarkerWithoutInviter()
    ,
    (error) => {
      log.error(error)

      // ログインユーザーの情報取得失敗時は、招待リンクとしては機能させない
      return resolveMarkerWithoutInviter()
    }
  )
}

/*
 * リファラル用マーカーの挿入箇所
 * src/Service/CustomTag/Contents/Loader/ReferralUrlContentLoader.php
 */
const marker = 'REPLACE__REFERRAL_URL__REPLACE'
const markerPattern = new RegExp(marker, 'g')

function resolveMarkerWithInviter(
  inviterId: string,
  loc: ReplaceReferralUrlDeps['location']
): ClientResolver {
  const inviteUrl = `${loc.protocol}//${loc.host}/referral/start/brand/${inviterId}`

  return {
    resolveString: src => src.replace(markerPattern, inviteUrl),
    resolveNode: src => replaceAllNodeContainsReferralUrlMarker(src, inviteUrl),
  }
}
/**
 * リファラル用マーカーを招待ユーザー情報無しで解決する
 *
 * 招待ユーザー情報が見つからない = 未ログインユーザーに対して表示されている = 運用ミスなので、
 * これといってエンドユーザーの操作に影響を与えない値に置換している.
 *
 * #を利用しているのは以下の理由から
 * - <a>タグのhref属性で利用された場合に、具体的な動作を伴わない
 * - <a>タグに限れば、同様の効果は javascript: void(0)でも達成できるが、他の文脈で使われた時に目立つ
 * - そもそも、非ログインページで利用されることは前提においていない
 *   - 非ログインページで挿入されている時点で運用ミスなので、最低限悪さをしない値でさえあればよしとしている
 *
 * @see https://github.com/Everforth/growthpalette-front/pull/8190#discussion_r1837558268
 */
function resolveMarkerWithoutInviter(): ClientResolver {
  return {
    resolveString: str => str.replace(markerPattern, '#'),
    resolveNode: str => replaceAllNodeContainsReferralUrlMarker(str, '#'),
  }
}

/**
 * ノード内のテキストノードおよび属性値に含まれるリファラル用マーカーを、指定された文字列に置換する
 *
 * ノードの探索を一段目のみXPath指定で行い、
 * その結果得られたノードに対してそれぞれ再起的に置換処理を行う.
 *
 * ChromeのDevtoolで計測した結果、同一のNodeツリーに対しては
 * 再帰的な全探索よりもXPath指定の方が高速である模様.
 * @see https://github.com/Everforth/growthpalette-front/issues/8284#issuecomment-2601775626
 *
 * 上記の結果から、おそらくXPath指定の方が探索より高速だが、
 * 以下のようなケースでdiv直下のテキストのみを抽出するのが上手くいかなかった.
 *
 * ```html
 * <div>
 *     <span>REPLACE__REFERRAL_URL__REPLACE</span>
 *     REPLACE__REFERRAL_URL__REPLACE
 *     <a href="REPLACE__REFERRAL_URL__REPLACE">REPLACE__REFERRAL_URL__REPLACE</a>
 * </div>
 * ```
 *
 * XPath指定の場合、div全体が取得され、div直下の "REPLACE__REFERRAL_URL__REPLACE" というTextNodeを直接参照できなかった
 * クエリの工夫次第ではうまく行くかもしれないが、この時点で一旦XPath指定で完結させることは見送った.
 *
 * XPath指定と再起的探索を組み合わせることで、
 * 素朴な全探索による処理時間増大を抑制しつつ、細かいNodeの処理にも対応した.
 */
const replaceAllNodeContainsReferralUrlMarker = (() => {
  const isElementNode = (node: Node): node is Element => {
    return node.nodeType === Node.ELEMENT_NODE
  }
  const isTextNode = (node: Node): node is Text => {
    return node.nodeType === Node.TEXT_NODE
  }
  const replaceMarker = (node: Node, to: string): void => {
    if (isElementNode(node)) {
      Array.from(node.attributes).forEach((attribute) => {
        if (attribute.value.includes(marker)) {
          attribute.value = attribute.value.replace(markerPattern, to)
        }
      })
    }
    if (isTextNode(node)) {
      if (node.textContent && node.textContent.includes(marker)) {
        node.textContent = node.textContent.replace(markerPattern, to)
      }
    }
  }

  return function traverseFirst(node: Node, to: string): Node {
    // scriptタグも含まれるようにすることもでき、また置換も実際可能ではあるが、
    // フリーコンテンツで埋め込まれるscriptはこの置換よりも先に完了するため、意味がない.
    //
    // この置換処理が前提として非同期処理を必要としており、
    // JSの実行系は非同期処理の完了を待たずに進行することが理由.
    //
    // async関数の中はawaitによって順次実行できるが、これはあくまでasync関数の範囲内のみ.
    // フリーコンテンツで埋め込まれるscriptは別のscriptブロックでありasync関数の範囲外であるため、
    // 置換処理が前提とする非同期処理の完了を待たずに実行される.
    //
    // top-level-await も調査したが、scriptブロックを跨いでの実行制御はできなかった.
    //
    // https://github.com/Everforth/growthpalette-front/issues/8284#issuecomment-2601849778
    // https://github.com/Everforth/growthpalette-front/issues/8284#issuecomment-2595201667
    //
    // なお、top-level-await 自体はES2022をサポートするモダンブラウザなら概ね利用可能.
    // https://caniuse.com/mdn-javascript_operators_await_top_level
    //
    // 以上の理由により、scriptタグの置換は可能だが意味がないため、scriptタグはあらかじめ除外している.
    // 操作対象をあらかじめ限定することで、処理時間を抑制する狙い.
    const xpath = `
      //*[not(ancestor-or-self::script) and
        (@*[. = '${marker}'] or text()[contains(., '${marker}')])
      ]
    `.trim()

    const matched = document.evaluate(xpath, node, null, XPathResult.ANY_TYPE, null)

    const targets: Node[] = (() => {
      const result: Node[] = []
      let elem = matched.iterateNext()
      while (elem) {
        // ここで取得したNodeを変更すると、iterateNextが例外を起こす.
        // そのため、該当のNodeは一旦退避しておいて、後でまとめて変更している
        result.push(elem)
        elem = matched.iterateNext()
      }

      return result
    })()

    targets.forEach((elem) => {
      replaceMarker(elem, to)
      /**
       * 子要素に対する処理も行わないと、DOM直下のテキスト置き換えが行われない.
       *
       * 「条件に合致するテキストを含むノード」はあくまでElementとして抽出され、
       * テキストそのものはTextNodeとしてElementの子として存在する点に注意.
       *
       * @see https://github.com/Everforth/growthpalette-front/pull/8289#discussion_r1923189932
       */
      elem.childNodes.forEach((child) => {
        replaceMarker(child, to)
      })
    })

    return node
  }
})()
